import { createProjectPath } from '@constant/gridgio/route-path';
import CollectionPlaceholder from '@design-system/collection-placeholder';
import React, { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTypedDispatch } from '@hook/common/useStore';
import { useProjectQuery } from '@hook/gridgio/useApi';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import PlanThumbnail from '@component/gridgio/project/plan/plan-thumbnail';
import Gap from '@component/common/gap';
import Canvas from '@design-system/canvas';
import TextButton from '@design-system/text-button';
import Chip from '@design-system/chip';
import Skeleton from '@design-system/skeleton';
import { ETheme } from '@datatype/type';
import * as color from '@constant/color';
import * as size from '@constant/size';

const PlanThumbnailContainer = styled.div`
  width: 120px;
  height: 80px;
  overflow: hidden;
  border-radius: ${size.BORDER_RADIUS_1};
`;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex-direction: column;
`;
const ProjectLink = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  align-self: flex-start;
  gap: 5px;
  width: 175px;
  padding: 0 0 0 5px;
  height: ${size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT};
  border: ${size.BORDER_WIDTH_1} solid ${color.BLACK_5};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.WHITE_1};
  cursor: pointer;
`;
const ControlContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${size.MARGIN_1};
`;
const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${size.MARGIN_1};
`;
const PlanItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${size.MARGIN_1};
`;
const PlanItemContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  flex-wrap: wrap;
  gap: ${size.MARGIN_2};
`;
const PlanInfoContainer = styled.div`
  display: flex;
  align-self: flex-start;
  min-width: 120px;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
`;
const BODY_DESIGN = { box: { width: '100%', alignX: 'left' }, text: { size: 'medium', color: color.GRAY_1 } } as const;

const Plan: React.FC = () => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const { projectKey = '' } = useParams();
  const scrollRef = useRef<HTMLDivElement>(null);
  const projectQuery = useProjectQuery(projectKey);
  const plans = projectQuery.data?.data.plans ?? [];
  const handleCreate = () => dispatch(modalActions.showDialogModal({ type: 'plan-create', data: { projectKey } }));
  const PlanItems = plans.map((plan) => {
    const source = plan.source;
    const name = plan.name ?? '';
    return (
      <Canvas key={plan.key} theme={ETheme.WHITE} width='100%' padding={size.MARGIN_1} border shadow>
        <PlanItemContainer>
          <PlanThumbnailContainer>
            <PlanThumbnail source={source} />
          </PlanThumbnailContainer>
          <PlanInfoContainer>
            <TextBlock
              text={name}
              design={{
                box: { width: '100%', alignX: 'left' },
                text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true },
              }}
            />
            <TextBlock
              text={source.filename}
              design={{ ...BODY_DESIGN, text: { size: 'small', color: color.GRAY_1, oneline: true } }}
            />
          </PlanInfoContainer>
          <ActionContainer>
            <IconBlock
              icon='edit'
              design={{
                box: { width: '35px', height: '35px', event: { cursor: 'pointer' } },
                icon: { size: 'small', color: color.GRAY_1 },
              }}
              click={{
                handler: () =>
                  dispatch(
                    modalActions.showDialogModal({
                      type: 'plan-edit',
                      data: {
                        projectKey,
                        planKey: plan.key,
                        name,
                        description: plan.description ?? undefined,
                      },
                    }),
                  ),
              }}
            />
            <IconBlock
              icon='trash'
              design={{
                box: { width: '35px', height: '35px', event: { cursor: 'pointer' } },
                icon: { size: 'small', color: color.GRAY_1 },
              }}
              click={{
                handler: () =>
                  dispatch(
                    modalActions.showDialogModal({
                      type: 'plan-remove',
                      data: { projectKey, planKey: plan.key },
                    }),
                  ),
              }}
            />
          </ActionContainer>
        </PlanItemContainer>
      </Canvas>
    );
  });

  const isLoading = projectQuery.isLoading;

  return (
    <Wrapper>
      <ProjectLink onClick={() => navigate(createProjectPath('project', projectKey))}>
        <IconBlock icon='left' design={{ icon: { size: 'small', color: color.BLACK_5 } }} />
        <TextBlock
          text='프로젝트로 돌아가기'
          design={{ text: { size: 'medium', color: color.BLACK_5, oneline: true } }}
        />
      </ProjectLink>
      <Gap height={size.MARGIN_1} />
      <TextBlock
        text='도면을 등록하고, 이 프로젝트의 작업에 사용하세요.'
        design={{ box: { alignX: 'left' }, text: { size: 'large', color: color.BLACK_5, bold: true } }}
      />
      <Gap height={size.MARGIN_1} />
      <ControlContainer>
        <Chip
          isLoading={isLoading}
          text={`프로젝트 도면 ${plans.length}개`}
          theme={ETheme.CYAN}
          kind='outlined'
          size='small'
          shape='round'
          usage='static'
        />
        <ActionContainer>
          <TextButton
            text='도면 등록'
            icon='add-file'
            arrangement='icon-text'
            theme={ETheme.CYAN}
            size='medium'
            shape='pill'
            width='120px'
            handleClick={handleCreate}
          />
        </ActionContainer>
      </ControlContainer>
      <Gap height={size.MARGIN_2} />
      <ScrollContainer width='100%'>
        <Scroll direction='y' height='100%' containerRef={scrollRef}>
          {isLoading ? (
            <PlanItemsContainer>
              <Skeleton tone='normal' height='90px' />
              <Skeleton tone='normal' height='90px' />
              <Skeleton tone='normal' height='90px' />
            </PlanItemsContainer>
          ) : plans.length === 0 ? (
            <CollectionPlaceholder
              theme={ETheme.CYAN}
              width='100%'
              height='100%'
              icon='files'
              title='첫 도면을 등록해보세요'
              description='PDF나 이미지 도면을 등록하고 작업을 시작하세요.'
              buttonText='도면 등록하기'
              handleButtonClick={handleCreate}
            />
          ) : (
            <>
              <PlanItemsContainer>{PlanItems}</PlanItemsContainer>
            </>
          )}
        </Scroll>
      </ScrollContainer>
    </Wrapper>
  );
};

export default Plan;
